/**
 * PersonalityGrid — 9 selectable cards (eneatypes 1..9).
 *
 * Pulls copy and accent palette from the existing decodificar content barrel
 * so the simulador and decodificar pages share a single visual language.
 *
 * Spec: docs/architecture/simulador-spec.md §5.
 */
import type { EnneagramTypeId } from "@/types/supabase";
import { TYPE_ACCENT, TYPE_CONTENT } from "@/features/decodificar/content";
import { cn } from "@/lib/utils";

export interface PersonalityGridProps {
  selected: number | null;
  onSelect: (eneatype: number) => void;
  disabled?: boolean;
}

const TYPE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

export function PersonalityGrid({ selected, onSelect, disabled }: PersonalityGridProps) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3"
      role="radiogroup"
      aria-label="Selecione a personalidade do paciente"
    >
      {TYPE_IDS.map((id) => {
        const tid = id as EnneagramTypeId;
        const accent = TYPE_ACCENT[tid];
        const content = TYPE_CONTENT[tid];
        const isSelected = selected === id;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onSelect(id)}
            className={cn(
              "relative text-left rounded-xl border bg-card p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isSelected
                ? "border-2 shadow-lg"
                : "border-border/50 hover:border-border",
              disabled && "opacity-50 cursor-not-allowed",
            )}
            style={
              isSelected
                ? { borderColor: accent, backgroundColor: `${accent}10` }
                : { borderLeftColor: accent, borderLeftWidth: 4 }
            }
            data-testid={`personality-card-${id}`}
          >
            <div className="flex items-center gap-3 mb-1.5">
              <span
                className="inline-flex items-center justify-center rounded-lg font-bold text-sm w-8 h-8 text-foreground"
                style={{ backgroundColor: `${accent}20`, color: accent }}
                aria-hidden="true"
              >
                {id}
              </span>
              <span className="font-medium text-foreground text-sm">
                {content.name}
              </span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {content.shortDescription}
            </p>
          </button>
        );
      })}
    </div>
  );
}
